import type { EditingMode } from '@/app/constants';
import { reportEvent } from '@/app/utils';
import type { EditingVideoSource } from '../store';
import { resolveStreamEditingType } from './modes';

/**
 * editing / editing-pro 点击埋点的 element_name 枚举。
 * 取值由数据侧文档定义，与组件内部命名无关，改动需同步数据侧。
 */
export const EDITING_CLICK_ELEMENT = {
  /** 切换输入方式（输入面板 tab、直播中切换、H5 左下角，三个入口同一个值） */
  switchInputType: 'switch_stream_input_type',
  /** 切换编辑模式（模式胶囊、侧边栏） */
  switchEditingType: 'switch_stream_editing_type',
  /** claim 成功、直播真正开起来 */
  startEditing: 'start_editing',
  /** 点选参考图素材（内置素材或已上传素材） */
  refImage: 'ref_image_click',
  /** 点「上传参考图」打开文件选择器 */
  refImageUpload: 'ref_image_upload_click',
  /** 结束录制（直播页挂断） */
  recordStop: 'record_stop_click',
  /** 结束页「再玩一次」 */
  resultPlayAgain: 'result_play_again_click',
  screenshot: 'screenshot_click',
  screenshotSave: 'screenshot_save_click',
  screenshotRetry: 'screenshot_retry_click',
  screenshotClose: 'screenshot_close_click',
} as const;

export type EditingClickElementName = (typeof EDITING_CLICK_ELEMENT)[keyof typeof EDITING_CLICK_ELEMENT];

/**
 * 视频来源 → `stream_input_type`。
 * 代码里「上传视频」叫 upload，数据侧要的字面值是 video，两边不同名，
 * 直接透传 EditingVideoSource 会上报成 upload。
 */
const STREAM_INPUT_TYPE_BY_SOURCE: Record<EditingVideoSource, string> = {
  camera: 'camera',
  upload: 'video',
  image: 'image',
  screen: 'screen',
};

export const resolveStreamInputType = (source: EditingVideoSource | null | undefined): string | undefined =>
  source ? STREAM_INPUT_TYPE_BY_SOURCE[source] : undefined;

/**
 * 界面上当前选中的输入方式。
 *
 * store 里的 editingVideoSource 刻意以 null 起步（有值就等于 getUserMedia），
 * 直到用户按下「开始创作」才写入。但输入面板默认就选中摄像头 tab，
 * 所以在用户确认之前，PV 和切模式这类事件应当上报 camera 而不是漏掉字段。
 * 与 EditingWorkspace 里 inputViewVideoSource 的兜底保持一致。
 */
export const resolveDisplayedVideoSource = (source: EditingVideoSource | null | undefined): EditingVideoSource =>
  source ?? 'camera';

/**
 * 从当前素材选择里取 `ref_image_id`：内置素材是 editingAssetId，上传素材是 s3 uri。
 * 两者互斥——上传素材没有 editingAssetId，内置素材的 referenceImageUri 是 CDN url 而非 s3 id。
 */
export const resolveTrackedRefImageId = (
  material:
    | { source?: string | null; editingAssetId?: string | null; referenceImageUri?: string | null }
    | null
    | undefined,
): string | null => {
  if (!material) return null;
  if (material.source === 'upload') return material.referenceImageUri ?? null;
  return material.editingAssetId ?? null;
};

type EditingTrackingContext = {
  editingMode: EditingMode;
  videoSource?: EditingVideoSource | null;
  /** 参考图标识：内置素材取 editingAssetId，上传素材取 s3 uri。 */
  refImageId?: string | null;
};

/**
 * 组装 editing 埋点的公共字段。
 * 空值字段不进 payload——漏斗里 `stream_input_type: ''` 和「没有这个字段」是两种含义，
 * 前者会让未选来源的点击在报表里凭空多出一档空串。
 */
export const buildEditingTrackingPayload = ({
  editingMode,
  videoSource,
  refImageId,
}: EditingTrackingContext): Record<string, string> => {
  const payload: Record<string, string> = {
    stream_editing_type: resolveStreamEditingType(editingMode),
  };

  const inputType = resolveStreamInputType(videoSource);
  if (inputType) payload.stream_input_type = inputType;
  if (refImageId) payload.ref_image_id = refImageId;

  return payload;
};

/**
 * editing / editing-pro 两页共用的点击上报。
 *
 * 口径由数据侧文档定义：事件名 `element_click`，不带 `page`，
 * 每条都带 `stream_editing_type` 与 `stream_input_type`，素材相关的额外带 `ref_image_id`。
 */
export const reportEditingElementClick = ({
  elementName,
  ...context
}: EditingTrackingContext & { elementName: EditingClickElementName }): void => {
  reportEvent('element_click', {
    element_name: elementName,
    ...buildEditingTrackingPayload(context),
  });
};
